import React, { createContext, useState, useContext, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import apiLinks from '../src/config/apiLinks.json';

const AuthContext = createContext();
export const useAuth = () => useContext(AuthContext);
const ONE_YEAR_MS = 1000 * 60 * 60 * 24 * 365;
const setLoginExpiry = () => {
  const expiresAt = Date.now() + ONE_YEAR_MS;
  localStorage.setItem('loginExpiresAt', expiresAt.toString());
};

const isLoginExpired = () => {
  const expiresAt = localStorage.getItem('loginExpiresAt');
  if (!expiresAt) return true;
  return Date.now() > Number(expiresAt);
};
export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [isBootstrapping, setIsBootstrapping] = useState(true);
  const [accessToken, setAccessToken] = useState(localStorage.getItem('accessToken') || '');
  const [FullName, setFullName] = useState(localStorage.getItem('fullName') || '');
  const [isTokenValid, setIsTokenValid] = useState(false);

  const navigate = useNavigate();
  // 🔹 CheckUser function (Step 1 - send OTP)
  const checkUser = async (username) => {
    try {
      console.log("check user fired!");
      const res = await axios.post(
        `${apiLinks.baseUrl}${apiLinks.Security.CheckUser}`,
        {
          Username: username,
          Password: ""
        },
        {
          withCredentials: true
        }
      );

      if (res.data?.sentSMS === "1") {
        return { success: true, changedPassword: res.data.changedPassword };
      }

      return {
        success: false,
        message: "ارسال رمز عبور ناموفق بود"
      };
    } catch (error) {
      //  اگر پاسخ از سرور دریافت شده (مثلاً 401 یا 502)
      if (error.response) {
        const status = error.response.status;

        if (status === 401) {
          return { success: false, message: "کد مشتری معتبر نیست" };
        }
        if (status >= 500) {
          return { success: false, message: "خطای سرور، لطفا بعداً تلاش کنید" };
        }
        return { success: false, message: `خطای ${status}: درخواست نامعتبر` };
      }

      // اگر اصلاً ارتباطی برقرار نشده (Network Error)
      else if (error.code === 'ERR_NETWORK' || !error.response) {
        return {
          success: false,
          message: "خطای شبکه: لطفا اتصال اینترنت خود را بررسی کنید"
        };
      }

      //  حالت پیش‌فرض برای خطاهای ناشناخته
      return { success: false, message: "خطای غیرمنتظره، لطفا دوباره تلاش کنید" };
    }
  };
  const ResendPassword = async (username) => {
    try {
      console.log("Resend Password fired!")
      const res = await axios.post(
        `${apiLinks.baseUrl}${apiLinks.Security.ResendPassword}`,
        {
          Username: username,
          Password: ""
        },
        {
          withCredentials: true
        }
      );
      if (res.data?.sentSMS === "1") {
        return { success: true };
      }

      return {
        success: false,
        message: "ارسال رمز عبور ناموفق بود"
      };
    } catch (error) {
      return {
        success: false,
        message: "کد مشتری معتبر نیست"
      };
    }
  };
  // 🔹 Login function
  const login = async (username, randomCode, changedPassword = 0) => {
    try {
      console.log("login fired!")
      const response = await axios.post(
        `${apiLinks.baseUrl}${apiLinks.Security.Login}?RandomCode=${randomCode}`,
        null,
        {
          withCredentials: true
        });        
      if (!response.data?.accessToken) throw new Error('رمز عبور اشتباه است');
      if (response.data?.accessToken) {
        // Successful login
        const { accessToken, refreshToken, fullName } = response.data;
        setUser({ username });
        setAccessToken(accessToken);
        setFullName(fullName);

        localStorage.setItem('username', username);
        localStorage.setItem('accessToken', accessToken);
        localStorage.setItem('refreshToken', refreshToken);
        localStorage.setItem('fullName', fullName);
        localStorage.setItem('1A1D286E-02C7-4AC4-AEBA-FC5BCB54471E', '1');
        localStorage.setItem('changedPassword', changedPassword?.toString() || "0");
        setLoginExpiry()
        await validateToken();
        // Return changedPassword so LoginPage can decide to show Step 3
        return changedPassword;
      } else if (response.data?.sentSMS === "2") {
        throw new Error('رمز عبور اشتباه است');
      } else {
        throw new Error('خطای نامشخص در ورود');
      }
    } catch (error) {
      console.error(error);
      throw new Error(error.response?.data?.message || error.message);
    }
  };
  const handleSetPassword = async (username, password, passwordRepeat) => {
    try {
      console.log("handleSetPassword fired!")
      const response = await axios.post(
        `${apiLinks.baseUrl}${apiLinks.User.UserSetPassword}?Password=${password}&passwordRepeat=${passwordRepeat}`,
        null,
        {
          withCredentials: true,
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
        }
      );
      // 2️⃣ Logout current session (clear old tokens)
      await logout(false);

      // 3️⃣ Login with the new password
      await loginByUserPassword(username, password);

      return true;
    } catch (err) {
      console.error('SetPassword failed:', err);
      throw new Error(err.response?.data?.message || 'خطا در تعریف رمز جدید');
    }
  };
  // 🔹 loginByUserPassword function
  const loginByUserPassword = async (username, password) => {
    try {
      console.log("loginByUserPassword fired!")
      const res = await axios.post(
        `${apiLinks.baseUrl}${apiLinks.Security.LoginByUserPassword}?Username=${username}&password=${password}`,
        null,
        { withCredentials: true }
      );

      const data = res.data;
      const isSuccess = data.accessToken && !data.sentSMS;
      if (!isSuccess && data.message) {
        // اگر توکن نداریم و پیام داریم، یعنی خطا رخ داده
        throw new Error(data.message);
      }
      // اگر پیام خطا نداشت، یعنی ورود موفق بوده است
      const { accessToken, fullName, changedPassword, refreshToken } = data;


      // Update context state
      setAccessToken(accessToken);
      setFullName(fullName);
      setIsTokenValid(true);

      // Update localStorage
      localStorage.setItem('username', username);
      localStorage.setItem('accessToken', accessToken);
      localStorage.setItem('refreshToken', refreshToken);
      localStorage.setItem('fullName', fullName);
      localStorage.setItem('1A1D286E-02C7-4AC4-AEBA-FC5BCB54471E', '1');
      localStorage.setItem('changedPassword', changedPassword?.toString() || "1");

      setLoginExpiry(); // update expiry
      setIsBootstrapping(false);
      return { success: true };


    } catch (err) {
      const errorMessage = err.response?.data?.message || err.message || 'خطای ناشناخته در ورود';
      throw new Error(errorMessage);
    }
  };

  const autoLogin = async () => {
    console.log("autologin is fired!")
    const isAuthorized =
      localStorage.getItem('1A1D286E-02C7-4AC4-AEBA-FC5BCB54471E') === '1';

    const username = localStorage.getItem('username');
    if (!isAuthorized || !username) return false;

    try {
      const res = await axios.post(
        `${apiLinks.baseUrl}${apiLinks.Security.AutoLogin}?Username=${username}`,
        null,
        { withCredentials: true }
      );
      if (!res.data?.accessToken) return false;

      const { accessToken, refreshToken, fullName, changedPassword } = res.data;

      setAccessToken(accessToken);
      setFullName(fullName);

      localStorage.setItem('accessToken', accessToken);
      localStorage.setItem('refreshToken', refreshToken);
      localStorage.setItem('fullName', fullName);
      localStorage.setItem('changedPassword', changedPassword?.toString() || "0");
      setLoginExpiry();
      await validateToken();

      return true;
    } catch (err) {
      console.error("AutoLogin failed:", err);
      return false;
    }
  };

  useEffect(() => {
    const bootstrap = async () => {
      const hasPhoneValidation =
        localStorage.getItem('1A1D286E-02C7-4AC4-AEBA-FC5BCB54471E') === '1';

      if (accessToken && !isLoginExpired()) {
        const valid = await validateToken();
        if (valid) {
          setIsBootstrapping(false);
          return;
        }
      }

      if (hasPhoneValidation) {
        const success = await autoLogin();
        if (success) {
          setIsBootstrapping(false);
          return;
        }
      }
      clearAllData();
      if (window.location.pathname !== '/login') {
        navigate('/login');
      }
      setIsBootstrapping(false);
    };

    bootstrap();
  }, []);

  // 🔹 Logout function
  const logout = async (navigateAfter = true) => {
    try {
      console.log("logout fired!")
      const accessToken = localStorage.getItem("accessToken");
      const refreshToken = localStorage.getItem("refreshToken");

      if (accessToken && refreshToken) {
        await axios.post(
          `${apiLinks.baseUrl}${apiLinks.Security.Logout}`,
          {
            refreshToken: refreshToken,
            accessToken: accessToken,
          },
          { withCredentials: true }
        );
      }
    } catch (error) {
    } finally {
      clearAllData();
      if (navigateAfter) navigate("/login");
    }
  };
  // 🔹 Clear session data
  const clearAllData = () => {

    localStorage.removeItem('accessToken');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('fullName');
    localStorage.removeItem('username');
    localStorage.removeItem('loginExpiresAt');
    localStorage.removeItem('1A1D286E-02C7-4AC4-AEBA-FC5BCB54471E');
    localStorage.removeItem('changedPassword');

    setAccessToken('');
    setUser(null);
    setIsTokenValid(false);
  };
  // 🔹 Validate Token
  const validateToken = async (token = accessToken) => {
    if (!token) {
      setIsTokenValid(false);
      return false;
    }

    try {
      const res = await axios.post(
        `${apiLinks.baseUrl}${apiLinks.Security.ValidateToken}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const valid = res.status === 200;
      setIsTokenValid(valid);
      return valid;
    } catch (err) {
      setIsTokenValid(false);
      return false;
    }
  };
  if (isBootstrapping) {
    return null;
  }
  return (
    <AuthContext.Provider
      value={{
        user,
        accessToken,
        isTokenValid,
        FullName,
        login,
        logout,
        validateToken,
        autoLogin,
        checkUser,
        handleSetPassword,
        loginByUserPassword,
        ResendPassword
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};